import { authenticatedApiService } from "../apiservice.auth";
import { TApiServiceOptions } from "../apiService";
import { API, rootAccessMgmtEndpoint } from "../../core/apiurl";
import {
  TRole,
  TRoleListResponse,
  TPermissionListResponse,
  TCreateRoleForm,
  TUpdateRoleForm,
  TRemovePermissionsFromRoleRequest,
  TRoleUsageInfo,
  TApiResponseWrapper,
  TEnhancedRoleListResponse,
  TEnhancedPermissionListResponse,
  TEnhancedRoleDetailResponse,
  TPermissionModulesResponse,
  TBulkPermissionAssignmentRequest,
  TBulkPermissionAssignmentResponse,
  TRoleListFilters,
  TPermissionListFilters,
  TPermissionKPIResponse,
  TAssignedPermissionListResponse,
  TRoleHierarchyPreview,
  THierarchyPlacement,
} from "./role-management.types";

class RoleManagementService {
  // Enhanced role list with search, filtering, and pagination
  getEnhancedRoleList(
    filters: TRoleListFilters = {},
    options?: TApiServiceOptions,
  ) {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const url = `${API.role_list}${queryString ? `?${queryString}&role_type=all` : "?role_type=all"}`;

    return authenticatedApiService.get<
      TApiResponseWrapper<TEnhancedRoleListResponse>
    >(url, options);
  }

  // Get enhanced role detail
  getEnhancedRoleDetail(roleId: string, options?: TApiServiceOptions) {
    return authenticatedApiService.get<
      TApiResponseWrapper<TEnhancedRoleDetailResponse>
    >(`${API.roleDetail}${roleId}/`, options);
  }

  // Get permissions with search, filtering, and role assignment context
  getEnhancedPermissions(
    filters: TPermissionListFilters = {},
    options?: TApiServiceOptions,
  ) {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const url = `${API.permissions}${queryString ? `?${queryString}` : ""}`;

    return authenticatedApiService.get<
      TApiResponseWrapper<TEnhancedPermissionListResponse>
    >(url, options);
  }

  // Get permission modules for filtering interface
  getPermissionModules(options?: TApiServiceOptions) {
    return authenticatedApiService.get<
      TApiResponseWrapper<TPermissionModulesResponse>
    >(`${API.rolePermissionModule}`, options);
  }

  // Bulk assign or remove permissions from a role
  bulkAssignPermissions(
    data: TBulkPermissionAssignmentRequest,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.post<
      TApiResponseWrapper<TBulkPermissionAssignmentResponse>
    >(`${API.roleAssignPermissions}`, data, options);
  }

  // Legacy methods for backward compatibility
  getRoleList(query = "", options?: TApiServiceOptions) {
    return authenticatedApiService.get<TApiResponseWrapper<TRoleListResponse>>(
      `${API.role_list}${query}`,
      options,
    );
  }

  getRoleById(id: string, options?: TApiServiceOptions) {
    return authenticatedApiService.get<
      TApiResponseWrapper<TEnhancedRoleDetailResponse>
    >(`${API.roleDetail}${id}/`, options);
  }

  createRole(data: TCreateRoleForm, options?: TApiServiceOptions) {
    return authenticatedApiService.post<TApiResponseWrapper<TRole>>(
      API.create_role,
      data,
      options,
    );
  }

  updateRole(data: TUpdateRoleForm, options?: TApiServiceOptions) {
    return authenticatedApiService.put<TApiResponseWrapper<TRole>>(
      `${API.update_role}${data.internal_id}/`,
      data,
      options,
    );
  }

  previewRoleHierarchy(
    payload: {
      hierarchy_placement?: THierarchyPlacement;
      role_internal_id?: string;
      new_role_name?: string;
    },
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.post<
      TApiResponseWrapper<TRoleHierarchyPreview>
    >(API.role_hierarchy_preview, payload, options);
  }

  deleteRole(internalId: string, options?: TApiServiceOptions) {
    return authenticatedApiService.delete(
      `${API.delete_role}${internalId}/`,
      options,
    );
  }

  getRoleUsageInfo(id: number, options?: TApiServiceOptions) {
    return authenticatedApiService.get<TApiResponseWrapper<TRoleUsageInfo>>(
      `${API.role_usage}/${id}/`,
      options,
    );
  }

  // Permission CRUD Operations
  getPermissionList(query = "", options?: TApiServiceOptions) {
    return authenticatedApiService.get<
      TApiResponseWrapper<TPermissionListResponse>
    >(`${API.listPermissions}${query}`, options);
  }

  removePermissionsFromRole(
    data: TRemovePermissionsFromRoleRequest,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.delete(`${API.removePermissionsToRole}`, {
      ...options,
      data,
    });
  }

  // Bulk operations
  bulkUpdateRoleStatus(
    roleIds: number[],
    isActive: boolean,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.patch(
      `${rootAccessMgmtEndpoint}/bulk-update-roles/`,
      {
        role_ids: roleIds,
        is_active: isActive,
      },
      options,
    );
  }

  // Permission KPI with filters
  getPermissionKPI(roleId: string, options?: TApiServiceOptions) {
    return authenticatedApiService.get<TPermissionKPIResponse>(
      `${API.permission_kpi}${roleId}/`,
      options,
    );
  }

  // AVAILABLE PERMISSIONS API
  getAvailablePermissions(
    roleId: string,
    query?: string,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.get<
      TApiResponseWrapper<TAssignedPermissionListResponse>
    >(
      `${API.available_permissions}${roleId}/${query ? `${query}` : ""}`,
      options,
    );
  }

  // ASSIGNED PERMISSIONS API
  getAssignedPermissions(
    roleId: string,
    query: string,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.get<
      TApiResponseWrapper<TAssignedPermissionListResponse>
    >(
      `${API.assigned_permissions}${roleId}/${query ? `${query}` : ""}`,
      options,
    );
  }
}

export const roleManagementService = new RoleManagementService();
